import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  Input,
  InputLabel,
  MenuItem,
  Paper,
  Select,
} from '@mui/material';
import { styled } from '@mui/material/styles';

interface Props {
  keyLabel: string
  keyOptions: string[],
  defaultFilters?: { [key: string]: string },
  filterValueSets?: Record<string, string[]>,
  onFilterChange?: (filters: { [key: string]: string }) => void,
}

const Outer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  padding: theme.spacing(2),
}));

const FilterCardInner = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  margin: theme.spacing(2),
}));

const ActionRow = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row-reverse',
  gap: theme.spacing(2),
}));

export const KeyValueFilterComponent: React.FC<Props> = ({ keyLabel, keyOptions, defaultFilters, filterValueSets, onFilterChange }: Props) => {
  const [filters, setFilters] = useState<{ key: string, value: string }[]>([]);

  useEffect(() => {
    setFilters(Object.keys(defaultFilters || {}).map((key) => ({
      key,
      value: defaultFilters![key]
    })));
  }, [defaultFilters])

  const isSetValue = useCallback((key: string) => {
    return Object.keys(filterValueSets || {}).includes(key);
  }, [filterValueSets]);

  const addFilter = () => {
    setFilters([
      ...filters,
      {
        key: '-1',
        value: ''
      }
    ])
  }

  const removeFilter = (idx: number) => {
    setFilters(filters.filter((_, i) => i !== idx));
  }

  const handleKeyChange = (idx: number, key: string) => {
    const newFilters = [...filters];
    newFilters[idx].key = key;
    setFilters(newFilters);
  }

  const handleValueChange = (idx: number, value: string) => {
    const newFilters = [...filters];
    newFilters[idx].value = value;
    setFilters(newFilters);
  }

  const handleApply = () => {
    if (!onFilterChange) {
      return
    }
    const filterObj = filters.reduce((acc, { key, value }) => {
      if (key && value) {
        acc[key] = value;
      }
      return acc;
    }, {} as { [key: string]: string });
    onFilterChange(filterObj);
  }

  const renderValueInput = (idx: number, key: string, value: string) => {
    if (isSetValue(key)) {
      return (
        <Select
          variant="standard"
          fullWidth
          value={value}
          onChange={(e) => handleValueChange(idx, e.target.value as string)}
        >
          {filterValueSets![key].map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </Select>
      )
    }
    return (
      <>
        <InputLabel>Value</InputLabel>
        <Input value={value} fullWidth onChange={(e) => handleValueChange(idx, e.target.value)} />
      </>
    )
  }

  return (
    <Outer>
      {filters.map(({ key, value }, idx) => (
        <Paper elevation={2} key={idx}>
          <FilterCardInner>
            <FormControl fullWidth variant="standard">
              <InputLabel>{keyLabel}</InputLabel>
              <Select
                variant="standard"
                fullWidth
                value={key}
                label={key}
                onChange={(e) => handleKeyChange(idx, e.target.value as string)}
              >
                <MenuItem value="-1">
                  {"- Select -"}
                </MenuItem>
                {keyOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth variant="standard">
              {renderValueInput(idx, key, value)}
            </FormControl>
            <Button disabled={idx < 3} onClick={() => removeFilter(idx)}>Remove</Button>
          </FilterCardInner>
        </Paper>
      ))}
      <ActionRow>
        <Button variant='contained' onClick={handleApply}>Apply</Button>
        <Button onClick={addFilter}>Add</Button>
      </ActionRow>
    </Outer>
  )
};
